// Shared building blocks for the Grammar Lab tools (कारक, सन्धि, समास, कृदन्त, वाच्य, ध्वनि-परिवर्तन): the page shell
// with the way back to the hub and the "more labs" strip, section headings with rule numbers, an in-page jump list
// and a small inline Prakrit span. Cards, buttons, chips and notices come from lab-ui, so every room looks the same.

import type { FC, ReactNode } from 'react'
import type { LangText } from '@/prakrit/components/grammar/labs/lab-ui'

import { cn } from '@/lib/utils'
import { CONTAINER, LabHeader, LabNav } from '@/prakrit/components/grammar/labs/lab-ui'

interface ToolPageProps {
    /** This tool's route, left out of the "more labs" strip. */
    current: string
    eyebrow: string
    title: LangText
    intro: LangText
    /** A shorter, simpler introduction for Kids mode. */
    kidsIntro?: LangText
    /** Rule numbers in the course book, e.g. "पाठ ८७". */
    source?: string
    /** Shown under the introduction (e.g. the jump list). */
    headerExtra?: ReactNode
    children: ReactNode
}

export const ToolPage: FC<ToolPageProps> = ({ current, eyebrow, title, intro, kidsIntro, source, headerExtra, children }): ReactNode => (
    <div className={CONTAINER}>
        <LabHeader eyebrow={eyebrow} title={title} intro={intro} kidsIntro={kidsIntro} source={source}>
            {headerExtra}
        </LabHeader>
        <div className='flex flex-col gap-14'>{children}</div>
        <LabNav current={current} />
        <div className='h-16' aria-hidden='true' />
    </div>
)

interface SectionHeadingProps {
    id?: string
    hi: string
    en: string
    /** Rule numbers in the course book, e.g. "नि.८१–९३". */
    rule?: string
    level?: 'h2' | 'h3'
    className?: string
    children?: ReactNode
}

export const SectionHeading: FC<SectionHeadingProps> = ({ id, hi, en, rule, level = 'h2', className, children }): ReactNode => {
    const Tag = level

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <Tag id={id} className='flex scroll-mt-24 flex-wrap items-baseline gap-x-3 gap-y-0.5'>
                <span lang='hi' className={cn('font-bold text-foreground', level === 'h2' ? 'text-2xl sm:text-[1.75rem]' : 'text-lg sm:text-xl')}>{hi}</span>
                <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>{en}</span>
            </Tag>
            {rule && <RuleRef rule={rule} />}
            {children}
        </div>
    )
}

export const RuleRef: FC<{ rule: string, className?: string }> = ({ rule, className }): ReactNode => (
    <p className={cn('text-xs text-muted-foreground', className)}>
        <span lang='hi' className='hindi'>{rule} · प्राकृत स्वयं-शिक्षक</span>
    </p>
)

/** Inline Prakrit inside Hindi or English text (not tappable). */
export const Pra: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => (
    <span lang='pra' className={cn('font-prakrit font-semibold text-prakrit', className)}>{children}</span>
)

export interface JumpLink {
    id: string
    hi: string
    en: string
}

/** Links to the sections of a long tool page. */
export const JumpNav: FC<{ links: JumpLink[], className?: string }> = ({ links, className }): ReactNode => (
    <nav aria-label='इस पृष्ठ पर · On this page' className={className}>
        <ul className='flex flex-wrap gap-2'>
            {links.map(l => (
                <li key={l.id}>
                    <a
                        href={`#${l.id}`}
                        className='inline-flex min-h-tap items-center gap-1.5 rounded-full border border-border bg-card/80 px-3.5 text-sm transition-colors hover:border-saffron/60 hover:bg-saffron/5'
                    >
                        <span lang='hi' className='hindi font-medium'>{l.hi}</span>
                        <span aria-hidden='true' className='ex-sep text-muted-foreground'>·</span>
                        <span lang='en' data-ex='en' className='text-xs text-muted-foreground'>{l.en}</span>
                    </a>
                </li>
            ))}
        </ul>
    </nav>
)
